import { Box } from '@mui/material';
import React from 'react';
import FundOpportunityCardData from '../../models/FundOpportunityCardData';
import ExtendedLiquid from '../../services/ExtendedLiquid';
import AssetImage from '../AssetImage';
import LinkifiedHtml from '../LinkifiedHtml';
import { Palette } from 'Palette';
import StyledCardLink from '../styles/StyledCardLink';
import StyledModal from '../styles/StyledModal';
import { StyledHeading, StyledTypography } from '../styles/styles';

interface PropertyCardImageZoomModalProps {
  derivedImage: string;
  altText: string | undefined;
  derivedTitle: string | undefined;
  processedTitle: string | undefined;
  processedDescription: string | undefined;
  tone: keyof typeof Palette;
  isHtmlDescription: boolean;
  footnotes: import('../../models/TopLevelTemplateComponentProps').FootNoteProps[] | undefined;
  cardData: FundOpportunityCardData | undefined;
  actions: React.ReactNode | undefined;
  linkable: boolean | undefined;
  printMode: boolean | undefined;
  expanded: boolean;
  onClose: () => void;
  onLinkClick: () => void;
}

export default function PropertyCardImageZoomModal({
  derivedImage,
  altText,
  derivedTitle,
  processedTitle,
  processedDescription,
  tone,
  isHtmlDescription,
  footnotes,
  cardData,
  actions,
  linkable,
  printMode,
  expanded,
  onClose,
  onLinkClick,
}: PropertyCardImageZoomModalProps) {
  return (
    <StyledModal
      key={`${derivedImage}-modal`}
      open={expanded}
      onClose={onClose}
      maxWidth="xl"
      fullWidth={false}
      tone="tone1"
      className="property-image-zoom-modal"
    >
      <Box
        key={`${derivedImage}-box`}
        sx={{
          maxWidth: '90vw',
          maxHeight: '90vh',
          overflow: 'auto',
          padding: '1rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
        }}
      >
        <Box
          sx={{
            border: '1px solid #E0E0E0',
            backgroundColor: Palette[tone].background,
            color: '#1C1C1C',
            borderRadius: '1.25rem',
            overflow: 'visible',
            width: '100%',
            maxWidth: 'min(600px, calc(90vw - 2rem))',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
            {footnotes && processedTitle !== derivedTitle ? (
              <LinkifiedHtml
                as={StyledHeading}
                headerVariant="subheading"
                tone={tone}
                display="inlineBlock"
                component="div"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  lineHeight: 1.3,
                  pb: 0,
                  pt: 0,
                  color: '#1C1C1C',
                }}
                html={processedTitle || ''}
              />
            ) : (
              <StyledHeading
                headerVariant="subheading"
                tone={tone}
                display="inlineBlock"
                component="div"
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  lineHeight: 1.3,
                  pb: 0,
                  pt: 0,
                  color: '#1C1C1C',
                }}
              >
                {processedTitle}
              </StyledHeading>
            )}
          </Box>

          {derivedImage && (
            <Box sx={{ p: 2, pb: 1 }}>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  overflow: 'hidden',
                  borderRadius: '0.75rem',
                }}
              >
                <AssetImage
                  url={derivedImage}
                  alt={altText || derivedTitle}
                  printMode={printMode}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '400px',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            </Box>
          )}

          {(processedDescription || actions || cardData?.investment !== undefined) && (
            <Box sx={{ px: 2, pt: 1, pb: 2 }}>
              {isHtmlDescription ? (
                <LinkifiedHtml
                  as={StyledTypography}
                  sx={{
                    mb: 2,
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    fontFamily: 'Inter, sans-serif',
                    color: '#4F4F4F',
                    '& p:first-of-type': { mt: 0, pt: 0 },
                    '& a.cms-autolink': {
                      color: 'inherit',
                      textDecoration: 'underline',
                    },
                  }}
                  component="div"
                  html={processedDescription || ''}
                />
              ) : (
                <StyledTypography
                  sx={{
                    mb: 2,
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    fontFamily: 'Inter, sans-serif',
                    color: '#4F4F4F',
                    '& p:first-of-type': { mt: 0, pt: 0 },
                  }}
                  component="div"
                >
                  {processedDescription}
                </StyledTypography>
              )}
              {cardData?.investment !== undefined && (
                <StyledTypography
                  sx={{
                    mt: 1,
                    fontFamily: 'Inter, sans-serif',
                    color: '#4F4F4F',
                  }}
                >
                  {cardData.investmentLabel}:{' '}
                  {ExtendedLiquid.currencyMillions(cardData.investment, true)}
                </StyledTypography>
              )}
              {actions && <Box sx={{ mt: 2 }}>{actions}</Box>}
            </Box>
          )}

          {linkable && (
            <Box sx={{ px: 2, pb: 2 }}>
              <StyledCardLink padding={1} onClick={onLinkClick} text="Find Out More" />
            </Box>
          )}
        </Box>
      </Box>
    </StyledModal>
  );
}
